// Admin API client. The historical-import commit hits the real backend
// (POST /ess/import). Categories and custodians have no backend route
// yet, so those calls are still served from a localStorage-backed shim
// so the UI is fully interactive; swap each body for the HTTP call in
// the adjacent comment when the route ships.

import { api } from "./client";
import { CATEGORIES as DEFAULT_CATEGORIES } from "../types/ess";
import type {
  Category,
  CategoryCreateInput,
  CategoryUpdateInput,
  Custodian,
  CustodianCreateInput,
  CustodianUpdateInput,
  ImportCommitResult,
  ImportValidationResult,
  ImportPreview,
  ImportRow,
} from "../types/admin";

const CATEGORY_KEY = "raven-ess:admin:categories";
const CUSTODIAN_KEY = "raven-ess:admin:custodians";

function now() {
  return new Date().toISOString();
}

function uid() {
  return (
    Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4)
  );
}

function loadCategories(): Category[] {
  const raw = localStorage.getItem(CATEGORY_KEY);
  if (raw) {
    try {
      return JSON.parse(raw) as Category[];
    } catch {
      /* fall through to seed */
    }
  }
  const seeded: Category[] = DEFAULT_CATEGORIES.map((label, idx) => ({
    id: uid(),
    label,
    sort_order: idx,
    active: true,
    created_at: now(),
    updated_at: now(),
  }));
  localStorage.setItem(CATEGORY_KEY, JSON.stringify(seeded));
  return seeded;
}

function saveCategories(list: Category[]) {
  localStorage.setItem(CATEGORY_KEY, JSON.stringify(list));
}

function loadCustodians(): Custodian[] {
  const raw = localStorage.getItem(CUSTODIAN_KEY);
  if (raw) {
    try {
      return JSON.parse(raw) as Custodian[];
    } catch {
      /* fall through */
    }
  }
  return [];
}

function saveCustodians(list: Custodian[]) {
  localStorage.setItem(CUSTODIAN_KEY, JSON.stringify(list));
}

function delay<T>(value: T, ms = 150): Promise<T> {
  return new Promise((resolve) => setTimeout(() => resolve(value), ms));
}

// ─── Categories ─────────────────────────────────────────────────────
// Backend: GET /admin/categories
export async function listCategories(): Promise<Category[]> {
  const list = loadCategories().sort((a, b) => a.sort_order - b.sort_order);
  return delay(list);
}

// Backend: POST /admin/categories
export async function createCategory(
  input: CategoryCreateInput,
): Promise<Category> {
  const list = loadCategories();
  const c: Category = {
    id: uid(),
    label: input.label.trim(),
    sort_order: list.length,
    active: true,
    created_at: now(),
    updated_at: now(),
  };
  saveCategories([...list, c]);
  return delay(c);
}

// Backend: PATCH /admin/categories/:id
export async function updateCategory(
  id: string,
  input: CategoryUpdateInput,
): Promise<Category> {
  const list = loadCategories();
  const idx = list.findIndex((c) => c.id === id);
  if (idx < 0) throw new Error("Category not found");
  const updated: Category = {
    ...list[idx],
    ...input,
    updated_at: now(),
  };
  list[idx] = updated;
  saveCategories(list);
  return delay(updated);
}

// Backend: DELETE /admin/categories/:id (soft delete via active=false)
export async function deactivateCategory(id: string): Promise<Category> {
  return updateCategory(id, { active: false });
}

// Backend: POST /admin/categories/reorder { ids: [...] }
export async function reorderCategories(ids: string[]): Promise<Category[]> {
  const list = loadCategories();
  const byId = new Map(list.map((c) => [c.id, c]));
  const reordered = ids
    .map((id, idx) => {
      const c = byId.get(id);
      if (!c) return null;
      return { ...c, sort_order: idx, updated_at: now() };
    })
    .filter((c): c is Category => c !== null);
  saveCategories(reordered);
  return delay(reordered);
}

// ─── Custodians ─────────────────────────────────────────────────────
// Backend: GET /admin/custodians
export async function listCustodians(): Promise<Custodian[]> {
  return delay(loadCustodians());
}

// Backend: POST /admin/custodians
export async function createCustodian(
  input: CustodianCreateInput,
): Promise<Custodian> {
  const list = loadCustodians();
  const c: Custodian = {
    id: uid(),
    name: input.name.trim(),
    email: input.email.trim().toLowerCase(),
    department: input.department?.trim() || null,
    role: input.role,
    active: true,
    created_at: now(),
    updated_at: now(),
  };
  saveCustodians([...list, c]);
  return delay(c);
}

// Backend: PATCH /admin/custodians/:id
export async function updateCustodian(
  id: string,
  input: CustodianUpdateInput,
): Promise<Custodian> {
  const list = loadCustodians();
  const idx = list.findIndex((c) => c.id === id);
  if (idx < 0) throw new Error("Custodian not found");
  const updated: Custodian = {
    ...list[idx],
    ...input,
    email: input.email
      ? input.email.trim().toLowerCase()
      : list[idx].email,
    department:
      input.department === undefined
        ? list[idx].department
        : input.department?.trim() || null,
    updated_at: now(),
  };
  list[idx] = updated;
  saveCustodians(list);
  return delay(updated);
}

// Backend: DELETE /admin/custodians/:id
export async function deleteCustodian(id: string): Promise<void> {
  const list = loadCustodians().filter((c) => c.id !== id);
  saveCustodians(list);
  return delay(undefined);
}

// ─── Historical import ──────────────────────────────────────────────
// The workbook is parsed and validated in the browser; only the rows
// that survive that pass are sent to the backend
// (POST /ess/import), which re-validates and reports per-row outcomes.

/** Excel serial-date decoder — the shape of `XLSX.SSF.parse_date_code`. */
export type DateCodeParser = (
  serial: number,
) => { y: number; m: number; d: number } | null;

/** Rows the backend accepts in one call. */
const IMPORT_CHUNK_SIZE = 500;

const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})/;
const SERIAL_RE = /^\d+(\.\d+)?$/;

function text(value: unknown): string {
  return value === null || value === undefined ? "" : String(value).trim();
}

function isoDate(y: number, m: number, d: number): string | null {
  const iso = `${String(y).padStart(4, "0")}-${String(m).padStart(2, "0")}-${String(
    d,
  ).padStart(2, "0")}`;
  const parsed = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return null;
  // Round-trip rejects overflow dates — `new Date("2026-13-45")` would
  // otherwise roll silently into the following year.
  return parsed.toISOString().slice(0, 10) === iso ? iso : null;
}

function fromSerial(
  serial: number,
  parseDateCode: DateCodeParser,
): { date: string | null; invalid: boolean } {
  const parts = parseDateCode(serial);
  const iso = parts ? isoDate(parts.y, parts.m, parts.d) : null;
  return iso ? { date: iso, invalid: false } : { date: null, invalid: true };
}

/**
 * Read the "Submitted On" cell as an ISO date. xlsx hands it over as an
 * Excel serial number (date-formatted cell) or a string; a JS Date is
 * accepted too, for a caller that parses with `cellDates`. A blank cell
 * is `{ date: null, invalid: false }` — the column is optional.
 */
export function toIsoDate(
  value: unknown,
  parseDateCode: DateCodeParser,
): { date: string | null; invalid: boolean } {
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return { date: null, invalid: true };
    const iso = isoDate(
      value.getFullYear(),
      value.getMonth() + 1,
      value.getDate(),
    );
    return iso ? { date: iso, invalid: false } : { date: null, invalid: true };
  }
  if (typeof value === "number") return fromSerial(value, parseDateCode);

  const raw = text(value);
  if (!raw) return { date: null, invalid: false };
  const parts = ISO_DATE_RE.exec(raw);
  if (parts) {
    const iso = isoDate(Number(parts[1]), Number(parts[2]), Number(parts[3]));
    return iso ? { date: iso, invalid: false } : { date: null, invalid: true };
  }
  if (SERIAL_RE.test(raw)) return fromSerial(Number(raw), parseDateCode);
  return { date: null, invalid: true };
}

/**
 * Turn one parsed sheet row into an `ImportRow`, carrying the
 * client-side validation errors that keep it out of the commit payload.
 * `index` is the row's position in the sheet body (0-based).
 *
 * Both the template header ("Employee Name") and the wire field name
 * ("employee_name") are accepted so a file exported from the backend can
 * be re-uploaded as-is.
 */
export function normaliseImportRow(
  raw: Record<string, unknown>,
  index: number,
  parseDateCode: DateCodeParser,
): ImportRow {
  const pick = (...keys: string[]): string =>
    keys.map((k) => text(raw[k])).find((v) => v !== "") ?? "";

  const submittedOn = toIsoDate(
    raw["Submitted On"] ?? raw["created_at"],
    parseDateCode,
  );

  const row: ImportRow = {
    // +2: sheet rows are 1-based and row 1 is the header.
    row_number: index + 2,
    suggestion_number: pick("Suggestion #", "suggestion_number") || null,
    employee_name: pick("Employee Name", "employee_name"),
    employee_id: pick("Employee ID", "employee_id"),
    department: pick("Department", "department"),
    category: pick("Category", "category"),
    other_category: pick("Other Category", "other_category") || null,
    title: pick("Title", "title"),
    description: pick("Description", "description"),
    submitted_on_behalf_of:
      pick("On Behalf Of", "submitted_on_behalf_of") || null,
    status: pick("Status", "status") || "submitted",
    created_at: submittedOn.date,
    remarks: pick("Remarks", "remarks") || null,
    errors: [],
  };

  if (!row.employee_name) row.errors.push("Missing employee name");
  if (!row.employee_id) row.errors.push("Missing employee ID");
  if (!row.department) row.errors.push("Missing department");
  if (!row.category) row.errors.push("Missing category");
  if (row.category.toLowerCase() === "others" && !row.other_category)
    row.errors.push('Please specify the category in "Other Category"');
  if (!row.title) row.errors.push("Missing title");
  if (!row.description) row.errors.push("Missing description");
  if (submittedOn.invalid) row.errors.push("Invalid submitted-on date");
  return row;
}

export async function previewImport(file: File): Promise<ImportPreview> {
  const XLSX = await import("xlsx");
  const buffer = await file.arrayBuffer();
  // No `cellDates` — xlsx 0.18.5 builds those Dates ~10s short of the
  // cell's midnight, so a date-formatted cell reads one day early in any
  // timezone ahead of UTC. Serials go through SSF.parse_date_code below,
  // which is exact and timezone-free.
  const book = XLSX.read(buffer, { type: "array" });
  const sheet = book.Sheets[book.SheetNames[0]];
  const raw = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, {
    defval: "",
  });
  // SSF reads serials against the 1900 epoch; a 1904-based workbook
  // (legacy Mac Excel) would otherwise land every date four years early.
  const offset = book.Workbook?.WBProps?.date1904 ? 1462 : 0;
  const parseDateCode: DateCodeParser = (serial) =>
    XLSX.SSF.parse_date_code(serial + offset);
  const rows = raw.map((r, i) => normaliseImportRow(r, i, parseDateCode));

  return {
    rows,
    valid_count: rows.filter((r) => r.errors.length === 0).length,
    invalid_count: rows.filter((r) => r.errors.length > 0).length,
  };
}

/**
 * Backend: POST /ess/import. Only rows that passed client-side
 * validation are sent, in chunks of `IMPORT_CHUNK_SIZE` (the per-call
 * cap). `skipped` in the result counts server-side rejections only — the
 * caller adds the rows it withheld here.
 */
export class ImportBatchError extends Error {
  constructor(public completed: ImportCommitResult, public uncertainRows: number[], public cause: unknown) {
    super("Import stopped. Some rows may already have been saved. Check the suggestions list before importing again.");
  }
}

export async function commitImport(
  rows: ImportRow[],
  onProgress?: (result: ImportCommitResult) => void,
): Promise<ImportCommitResult> {
  const payload: Omit<ImportRow, "errors">[] = rows
    .filter((r) => r.errors.length === 0)
    .map(({ errors, ...row }) => row);

  const combined: ImportCommitResult = { imported: 0, skipped: 0, results: [] };
  for (let i = 0; i < payload.length; i += IMPORT_CHUNK_SIZE) {
    const batch = payload.slice(i, i + IMPORT_CHUNK_SIZE);
    let data: ImportCommitResult;
    try {
      ({ data } = await api.post<ImportCommitResult>("/ess/import", { rows: batch }));
    } catch (error) {
      throw new ImportBatchError(combined, batch.map(row => row.row_number), error);
    }
    combined.imported += data.imported;
    combined.skipped += data.skipped;
    combined.results.push(...(data.results ?? []));
    onProgress?.({ ...combined, results: [...combined.results] });
  }
  return combined;
}

export async function mapSuggestionImportColumns(columns: string[], sample_rows: Record<string, unknown>[]) {
  const { data } = await api.post<Record<string, string | null>>("/ess/import/map-columns", { columns, sample_rows });
  return data;
}

/** Check rows against current server data without writing any suggestions. */
export async function validateImport(rows: ImportRow[]): Promise<ImportValidationResult> {
  const payload = rows.filter(row => !row.errors.length).map(({ errors, ...row }) => row);
  const combined: ImportValidationResult = { ready: 0, blocked: 0, results: [] };
  for (let i = 0; i < payload.length; i += IMPORT_CHUNK_SIZE) {
    const { data } = await api.post<ImportValidationResult>("/ess/import/validate", {
      rows: payload.slice(i, i + IMPORT_CHUNK_SIZE),
    });
    const batch = payload.slice(i, i + IMPORT_CHUNK_SIZE);
    if (data.results.length !== batch.length || batch.some(row =>
      data.results.filter(result => result.row_number === row.row_number).length !== 1)) {
      throw new Error("Server validation did not check every row. Please try again.");
    }
    combined.ready += data.ready;
    combined.blocked += data.blocked;
    combined.results.push(...data.results);
  }
  return combined;
}
